import { auth, db, supabase } from './supabase.js'

export class GameLogic {
  constructor() {
    this.maxAttempts = 3
    this.maxHints = 3
  }

  // Initialize a new game session
  createGameSession(song, genre, difficulty, gameDate) {
    return {
      song,
      genre,
      difficulty,
      gameDate: gameDate.toISOString().split('T')[0], // YYYY-MM-DD format
      attempts: 1,
      hintsUsed: 0,
      userGuesses: new Array(song.blanks.length).fill(''),
      gameOver: false,
      won: false,
      startTime: Date.now(),
      endTime: null
    }
  }

  // Check if a guess is correct, partial, or incorrect
  evaluateGuess(userGuess, correctAnswer) {
    const guess = userGuess.trim().toLowerCase()
    const correct = correctAnswer.toLowerCase()
    
    if (guess === correct) {
      return 'correct'
    }
    
    if (this.isPartialMatch(guess, correct)) {
      return 'partial'
    }
    
    return 'incorrect'
  }

  // Check for partial matches (close but not exact)
  isPartialMatch(guess, correct) {
    if (!guess || !correct) return false
    
    // Allow for minor length differences
    if (Math.abs(guess.length - correct.length) <= 2) {
      let matches = 0
      const minLength = Math.min(guess.length, correct.length)
      
      for (let i = 0; i < minLength; i++) {
        if (guess[i] === correct[i]) matches++
      }
      
      // Consider it a partial match if 60% or more characters match
      return matches / correct.length >= 0.6
    }
    
    // Check for common word variations
    return this.checkWordVariations(guess, correct)
  }

  // Check for common word variations (plurals, tenses, etc.)
  checkWordVariations(guess, correct) {
    const variations = [
      // Plurals
      [guess + 's', correct],
      [guess, correct + 's'],
      [guess + 'es', correct],
      [guess, correct + 'es'],
      
      // Past tense
      [guess + 'ed', correct],
      [guess, correct + 'ed'],
      [guess + 'd', correct],
      [guess, correct + 'd'],
      
      // Present participle
      [guess + 'ing', correct],
      [guess, correct + 'ing'],
      
      // Common contractions
      [guess.replace("'", ''), correct],
      [guess, correct.replace("'", '')],
    ]
    
    return variations.some(([a, b]) => a === b)
  }

  // Evaluate all guesses for the current attempt
  evaluateAllGuesses(gameSession) {
    const results = gameSession.song.blanks.map((correctAnswer, index) => {
      const userGuess = gameSession.userGuesses[index] || ''
      return {
        index,
        userGuess,
        correctAnswer,
        evaluation: this.evaluateGuess(userGuess, correctAnswer)
      }
    })
    
    // Check if all answers are correct
    const allCorrect = results.every(result => result.evaluation === 'correct')
    const hasGuesses = results.some(result => result.userGuess.trim())
    
    return {
      results,
      allCorrect,
      hasGuesses,
      correctCount: results.filter(r => r.evaluation === 'correct').length,
      partialCount: results.filter(r => r.evaluation === 'partial').length,
      incorrectCount: results.filter(r => r.evaluation === 'incorrect').length
    }
  }

  // Process a game submission
  processSubmission(gameSession) {
    const evaluation = this.evaluateAllGuesses(gameSession)
    
    if (!evaluation.hasGuesses) {
      return {
        error: 'Please fill in at least one word before submitting!',
        gameSession
      }
    }
    
    // Update game session
    const updatedSession = { ...gameSession }
    
    if (evaluation.allCorrect) {
      // Player won!
      updatedSession.gameOver = true
      updatedSession.won = true
      updatedSession.endTime = Date.now()
    } else if (gameSession.attempts >= this.maxAttempts) {
      // Game over - no more attempts
      updatedSession.gameOver = true
      updatedSession.won = false
      updatedSession.endTime = Date.now()
    } else {
      // Continue playing
      updatedSession.attempts++
    }
    
    return {
      success: true,
      evaluation,
      gameSession: updatedSession
    }
  }

  // Get a hint for a specific blank
getHint(gameSession, blankIndex = null) {
  if (gameSession.hintsUsed >= this.maxHints) {
    return {
      error: 'No more hints available!'
    }
  }
  
  // If a specific blank was provided, use it
  if (blankIndex !== null && blankIndex >= 0 && blankIndex < gameSession.song.blanks.length) {
    // Check if this blank already has a correct answer
    const userGuess = gameSession.userGuesses[blankIndex] || ''
    const correctAnswer = gameSession.song.blanks[blankIndex]
    
    if (this.evaluateGuess(userGuess, correctAnswer) === 'correct') {
      // This blank is already correct, find another one
      blankIndex = this.findBestHintTarget(gameSession)
      if (blankIndex === -1) {
        return {
          error: 'All blanks are already correct!'
        }
      }
    }
  } else {
    // No specific blank requested or invalid index, find the best one
    blankIndex = this.findBestHintTarget(gameSession)
  }
  
  if (blankIndex === -1 || blankIndex >= gameSession.song.hints.length) {
    return {
      error: 'No suitable blank found for hint'
    }
  }
  
  const hint = gameSession.song.hints[blankIndex]
  const updatedSession = {
    ...gameSession,
    hintsUsed: gameSession.hintsUsed + 1
  }
  
  return {
    success: true,
    hint,
    blankIndex,
    gameSession: updatedSession
  }
}

  // Find the best blank to give a hint for
  findBestHintTarget(gameSession) {
    // First, look for empty blanks
    for (let i = 0; i < gameSession.song.blanks.length; i++) {
      if (!gameSession.userGuesses[i] || !gameSession.userGuesses[i].trim()) {
        return i
      }
    }
    
    // If all blanks are filled, look for incorrect ones
    for (let i = 0; i < gameSession.song.blanks.length; i++) {
      const guess = gameSession.userGuesses[i] || ''
      const correct = gameSession.song.blanks[i]
      if (this.evaluateGuess(guess, correct) !== 'correct') {
        return i
      }
    }
    
    return -1 // No suitable target found
  }

  // Calculate game score
  calculateScore(gameSession) {
    if (!gameSession.gameOver) {
      return null
    }
    
    const baseScore = gameSession.won ? 1000 : 0
    const attemptsBonus = gameSession.won ? (this.maxAttempts - gameSession.attempts) * 100 : 0
    const hintsBonus = gameSession.won ? (this.maxHints - gameSession.hintsUsed) * 50 : 0
    const timeBonus = gameSession.won && gameSession.endTime ? 
      Math.max(0, 300 - Math.floor((gameSession.endTime - gameSession.startTime) / 1000)) : 0
    
    return {
      baseScore,
      attemptsBonus,
      hintsBonus,
      timeBonus,
      totalScore: baseScore + attemptsBonus + hintsBonus + timeBonus
    }
  }

  // Save game result to database
async saveGameResult(gameSession, user = null) {
  if (!user) {
    try {
      user = await auth.getUser()
    } catch (error) {
      console.log('No authenticated user, skipping save')
      return null
    }
  }
  
  if (!user || !gameSession.gameOver) {
    console.log('Skipping save:', { hasUser: !!user, gameOver: gameSession.gameOver })
    return null
  }
  
  const score = this.calculateScore(gameSession)
  const playTime = gameSession.endTime ? 
    Math.floor((gameSession.endTime - gameSession.startTime) / 1000) : null
  
  const gameResult = {
    user_id: user.id,
    song_id: gameSession.song.id,
    game_date: gameSession.gameDate,
    genre: gameSession.genre,
    difficulty: gameSession.difficulty,
    attempts: gameSession.attempts,
    completed: gameSession.gameOver,
    won: gameSession.won,
    hints_used: gameSession.hintsUsed,
    score: score?.totalScore || 0,
    play_time_seconds: playTime
  }
  
  console.log('Attempting to save game result:', gameResult)
  
  try {
    const result = await db.saveGameResult(gameResult)
    console.log('Game result saved successfully:', result)
    
    // Let's immediately check if it was saved
    const { data: checkSave } = await supabase
      .from('game_results')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .single()
    
    console.log('Verification - Last saved game:', checkSave)
    
    return result
  } catch (error) {
    console.error('Failed to save game result:', error)
    return null
  }
}

  // Load existing game result
async loadGameResult(song, genre, difficulty, gameDate, user = null) {
  if (!user) {
    try {
      user = await auth.getUser()
      console.log('LoadGameResult - fetched user:', user)
    } catch (error) {
      console.log('LoadGameResult - no user found')
      return null
    }
  }
  
  if (!user) return null
  
  try {
    const dateString = gameDate.toISOString().split('T')[0]
    console.log('LoadGameResult - calling getGameResult with:', {
      userId: user.id,
      songId: song.id,
      dateString,
      genre,
      difficulty
    })
    
    // Check if we're authenticated
    const { data: { session } } = await supabase.auth.getSession()
    console.log('Current session:', session)
    
    return await db.getGameResult(user.id, song.id, dateString, genre, difficulty)
  } catch (error) {
    console.error('Failed to load game result:', error)
    return null
  }
}

  // Generate shareable result text
  generateShareText(gameSession) {
    const attempts = gameSession.gameOver ? 
      (gameSession.won ? gameSession.attempts : 'X') : gameSession.attempts
    const emoji = gameSession.won ? '🎵' : '❌'
    const difficulty = gameSession.difficulty === 'hard' ? 'Hard' : 'Easy'
    const dateStr = new Date(gameSession.gameDate).toDateString()
    const genreDisplay = gameSession.genre === 'daily' ? 'Daily Hit' : 
      gameSession.genre.charAt(0).toUpperCase() + gameSession.genre.slice(1)
    
    return `LyricGuesser ${emoji}\n${gameSession.song.title} - ${gameSession.song.artist}\n${dateStr} | ${genreDisplay} (${difficulty})\nAttempts: ${attempts}/6\n\nPlay at: ${window.location.href}`
  }

  // Validate game session
  validateGameSession(gameSession) {
    const required = ['song', 'genre', 'difficulty', 'gameDate', 'attempts', 'userGuesses']
    
    for (const field of required) {
      if (gameSession[field] === undefined || gameSession[field] === null) {
        return { valid: false, error: `Missing required field: ${field}` }
      }
    }
    
    if (gameSession.attempts < 1 || gameSession.attempts > this.maxAttempts) {
      return { valid: false, error: 'Invalid attempt count' }
    }
    
    if (gameSession.hintsUsed < 0 || gameSession.hintsUsed > this.maxHints) {
      return { valid: false, error: 'Invalid hints used count' }
    }
    
    if (!Array.isArray(gameSession.userGuesses)) {
      return { valid: false, error: 'User guesses must be an array' }
    }
    
    if (gameSession.userGuesses.length !== gameSession.song.blanks.length) {
      return { valid: false, error: 'User guesses length must match song blanks length' }
    }
    
    return { valid: true }
  }
}

// Create singleton instance
export const gameLogic = new GameLogic()